import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Dropdown, Menu } from 'antd';
import { TbPlus, TbUserCheck, TbUserCircle, TbUserHexagon, TbUserPentagon } from 'react-icons/tb';
import { useParams } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { teamService } from '@/services';
import { DropdownMenuPanel, VirtualizedList as List } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useMemberStore } from '@/stores';
import { SetStateMethod, Team, TeammatePermissionType } from '@/types';

interface ListItemProps {
    result: any;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    collaborators: any;
    setCollaborators: any;
    team?: Team | any;
}

const ListItem: FC<ListItemProps> = ({ result, open, setOpen, setCollaborators, collaborators, team }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    const [permission, setPermission] = useState(TeammatePermissionType.MEMBER);

    const { selectedMember: member } = useMemberStore();

    const alreadyInvited = collaborators?.some((i: any) => i?.member?.id === result?.id);
    const disabled = member?.id === result?.id || alreadyInvited;

    useEffect(() => {
        setLoading(false);
    }, []);

    const { teamId: routeTeamId } = useParams<{ teamId: string }>();
    const teamId = team?.id ?? routeTeamId;

    const handleInviteWorkspaceMemberToTeam = async () => {
        if (!teamId) {
            return;
        }

        try {
            const teammate = await teamService.inviteWorkspaceMember(teamId, {
                inviteeId: result['user']?.id,
                permission,
            });

            setOpen(false);

            setCollaborators([...collaborators, teammate]);

            toast('Member Added to Team', {
                icon: <TbUserCheck size='1rem' />,
                description: `${result['user']?.firstName} ${result['user']?.lastName}`,
            });
        } catch (error) {
            captureException(error);
        }
    };

    const { token } = useAntDesignTheme();

    return (
        <>
            <List.Item
                key={result?.id}
                extra={[
                    <Dropdown
                        placement='bottomRight'
                        trigger={['click']}
                        key='permissionDropdown'
                        popupRender={() => (
                            <DropdownMenuPanel
                                selectedKeys={[permission]}
                                onClick={(e: any) => {
                                    setPermission(e.key);
                                }}
                            >
                                <Menu.Item key={TeammatePermissionType.MEMBER}>
                                    <TbUserHexagon
                                        size='1rem'
                                        style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                                    />
                                    Member
                                    <br />
                                    <small style={{ color: token.colorTextTertiary }}>
                                        Ability to view, collaborate, and invite members to the team.
                                    </small>
                                </Menu.Item>
                                <Menu.Item key={TeammatePermissionType.ADMIN}>
                                    <TbUserPentagon
                                        size='1rem'
                                        style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                                    />
                                    Admin
                                    <br />
                                    <small style={{ color: token.colorTextTertiary }}>
                                        Full access to investments and other resources on the team.
                                    </small>
                                </Menu.Item>
                                <Menu.Item key={TeammatePermissionType.GUEST}>
                                    <TbUserCircle
                                        size='1rem'
                                        style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                                    />
                                    Guest
                                    <br />
                                    <small style={{ color: token.colorTextTertiary }}>
                                        Access limited to select items on the team.
                                    </small>
                                </Menu.Item>
                            </DropdownMenuPanel>
                        )}
                    >
                        <DropdownButton type='text' style={{ marginRight: '0.5rem' }}>
                            {permission}
                        </DropdownButton>
                    </Dropdown>,
                    <Button
                        disabled={disabled}
                        key='inviteButton'
                        type='dashed'
                        icon={<TbPlus size='1rem' style={{ marginRight: '0.25rem', verticalAlign: '-0.2rem' }} />}
                        onClick={handleInviteWorkspaceMemberToTeam}
                    >
                        Invite
                    </Button>,
                ]}
            >
                <List.Item.Meta
                    avatar={<Avatar src={result['user']?.avatar} style={{ verticalAlign: '-1.5rem' }} />}
                    title={`${result['user']?.firstName} ${result['user']?.lastName}`}
                    description={result['user']?.emailAddress}
                />
            </List.Item>
        </>
    );
};

export default ListItem;
